/* Created by Interactive 63 - https://interactive63.com */
/**
 * IndexedDB storage for projects: full records, a recent-projects index, and the ID of the
 * last opened project. Every record read back is treated as untrusted and validated.
 */
import { createStore, del, get, set, type UseStore } from 'idb-keyval';
import type { Project } from '../../core/model/types';
import { readProjectRecord, type ProjectRecordResult, type Sanitiser } from './project-record';

export interface IndexEntry {
  id: string;
  name: string;
  updatedAt: string;
  thumbnail: string | null;
}

export const MAX_PROJECTS = 30;
export const MAX_THUMBNAIL_BYTES = 60 * 1024;
const THUMBNAIL_PREFIX = 'data:image/svg+xml;charset=utf-8,';

/** Keeps a thumbnail only if it is a small SVG data URL. */
export function acceptThumbnail(value: unknown): string | null {
  return typeof value === 'string' &&
    value.startsWith(THUMBNAIL_PREFIX) &&
    value.length <= MAX_THUMBNAIL_BYTES
    ? value
    : null;
}

/** Builds a thumbnail data URL from SVG markup, or null if it exceeds the size cap. */
export function thumbnailFromMarkup(markup: string): string | null {
  return acceptThumbnail(`${THUMBNAIL_PREFIX}${encodeURIComponent(markup)}`);
}

/** Reads the index defensively, dropping malformed entries. */
function cleanIndex(value: unknown): IndexEntry[] {
  if (!Array.isArray(value)) return [];
  const entries: IndexEntry[] = [];
  for (const item of value as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const entry = item as Record<string, unknown>;
    if (typeof entry['id'] !== 'string' || typeof entry['name'] !== 'string') continue;
    entries.push({
      id: entry['id'],
      name: entry['name'],
      updatedAt: typeof entry['updatedAt'] === 'string' ? entry['updatedAt'] : '',
      thumbnail: acceptThumbnail(entry['thumbnail']),
    });
  }
  return entries;
}

export class ProjectStorage {
  private store: UseStore | undefined;
  private queue: Promise<unknown> = Promise.resolve();

  /** Creates storage backed by the named database; the connection opens on first use. */
  constructor(
    private readonly dbName = 'svg-tempo',
    private readonly sanitise?: Sanitiser,
  ) {}

  /** Lists projects, newest first. */
  async list(): Promise<IndexEntry[]> {
    return this.serialise(() => this.readIndex());
  }

  /** Whether a project with this ID is stored. */
  async has(id: string): Promise<boolean> {
    return (await this.list()).some((entry) => entry.id === id);
  }

  /** Saves a project and updates the index, evicting the oldest others beyond the cap. */
  async save(project: Project, thumbnail: string | null): Promise<void> {
    await this.serialise(async () => {
      await set(`project:${project.id}`, project, this.db());
      const rest = (await this.readIndex()).filter((entry) => entry.id !== project.id);
      const index = [
        { id: project.id, name: project.name, updatedAt: project.updatedAt, thumbnail },
        ...rest,
      ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
      // The project being saved is the open one, so it is never the eviction candidate.
      while (index.length > MAX_PROJECTS) {
        const oldest = [...index].reverse().find((entry) => entry.id !== project.id);
        if (!oldest) break;
        index.splice(index.indexOf(oldest), 1);
        await del(`project:${oldest.id}`, this.db());
      }
      await set('index', index, this.db());
    });
  }

  /** Loads and validates one project. */
  async load(id: string): Promise<ProjectRecordResult | undefined> {
    const raw: unknown = await this.serialise(() => get(`project:${id}`, this.db()));
    return raw === undefined ? undefined : readProjectRecord(raw, this.sanitise);
  }

  /** Removes a project and its index entry. */
  async remove(id: string): Promise<void> {
    await this.serialise(async () => {
      await del(`project:${id}`, this.db());
      const index = (await this.readIndex()).filter((entry) => entry.id !== id);
      await set('index', index, this.db());
    });
  }

  /** The ID of the project to reopen on start-up, if any. */
  async lastOpenId(): Promise<string | undefined> {
    const value: unknown = await this.serialise(() => get('lastOpenId', this.db()));
    return typeof value === 'string' ? value : undefined;
  }

  /** Records which project to reopen on start-up, or clears it. */
  async setLastOpenId(id: string | undefined): Promise<void> {
    await this.serialise(() =>
      id === undefined ? del('lastOpenId', this.db()) : set('lastOpenId', id, this.db()),
    );
  }

  /** Opens the database on first use. Throws where IndexedDB is unavailable. */
  private db(): UseStore {
    this.store ??= createStore(this.dbName, 'projects');
    return this.store;
  }

  /** Reads the index. Must run inside `serialise`. */
  private async readIndex(): Promise<IndexEntry[]> {
    return cleanIndex(await get('index', this.db()));
  }

  /** Runs operations one at a time so index read-modify-write cycles never interleave. */
  private serialise<T>(operation: () => Promise<T>): Promise<T> {
    const next = this.queue.then(
      () => operation(),
      () => operation(),
    );
    this.queue = next.catch(() => undefined);
    return next;
  }
}
